import React, { createContext, useContext, useReducer, useEffect } from 'react';

/* ============================================================================
 * FormDepartureContext — South Yarra ticket wizard state.
 * Renamed from FormContext so it never collides with the workflow app's copy.
 * Exports: FormDepartureProvider, useFormDepartureContext, isOptionalField
 * ==========================================================================*/

const initialFormState = {
  formData: {
    // Personal info (pre-filled from the session)
    company: '',
    requestName: '',
    email: '',

    // Ticket info
    jobTitle: '',
    jobType: '',
    jobDescription: '',

    // Date and time
    completedDate: '',
    completedTime: '',

    // Photos
    photoUpload: null,
    photos: [],

    // Preset hidden fields
    startdate: '',
    returnDate: '',
    clientApproval: 'Pending Approval',
    activeTicket: 'Active Ticket',
    status: 'Pending',
  },
  formErrors: {},
  visitedSections: [],
  modifiedFields: new Set(),
  currentStep: 0,
  isSubmitting: false,
  submitMessage: null,
  completedSteps: [],
  seed: null,               // the userData the page seeded us with (kept across resets)
};

function formReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD_VALUE':
      return {
        ...state,
        formData: { ...state.formData, [action.field]: action.value },
        modifiedFields: new Set([...state.modifiedFields, action.field]),
      };
    case 'SET_FILE':
      return {
        ...state,
        formData: { ...state.formData, [action.field]: action.file },
        modifiedFields: new Set([...state.modifiedFields, action.field]),
      };
    case 'SET_FORM_ERRORS':
      return { ...state, formErrors: action.errors };
    case 'SET_CURRENT_STEP':
      return {
        ...state,
        currentStep: action.step,
        visitedSections: [...new Set([...state.visitedSections, action.step])],
      };
    case 'MARK_STEP_COMPLETED':
      return { ...state, completedSteps: [...new Set([...state.completedSteps, action.step])] };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.isSubmitting };
    case 'SET_SUBMIT_MESSAGE':
      return { ...state, submitMessage: action.message };
    case 'SET_MODIFIED_FIELDS':
      return { ...state, modifiedFields: action.fields };
    case 'INITIALIZE_USER_DATA':
      return { ...state, formData: { ...state.formData, ...action.userData }, seed: action.userData };
    case 'RESET_FORM':
      // Back to step 0 with a clean ticket, but Company / Request Name / Email
      // (and any other seed values) stay populated from the session.
      return {
        ...initialFormState,
        formData: { ...initialFormState.formData, ...(state.seed || {}) },
        seed: state.seed,
      };
    default:
      return state;
  }
}

const FormDepartureContext = createContext();

export const useFormDepartureContext = () => useContext(FormDepartureContext);

// Hook for per-field optionality; everything is required by default
export const isOptionalField = (field, formData) => false;

export const FormDepartureProvider = ({ children, userData, brand }) => {
  const [state, dispatch] = useReducer(formReducer, initialFormState);

  useEffect(() => {
    if (userData) {
      dispatch({ type: 'INITIALIZE_USER_DATA', userData });
    }
  }, [userData]);

  const setFieldValue = (field, value) => dispatch({ type: 'SET_FIELD_VALUE', field, value });
  const setFile = (field, file) => dispatch({ type: 'SET_FILE', field, file });
  const setCurrentStep = (step) => dispatch({ type: 'SET_CURRENT_STEP', step });
  const markStepCompleted = (step) => dispatch({ type: 'MARK_STEP_COMPLETED', step });
  const setSubmitting = (isSubmitting) => dispatch({ type: 'SET_SUBMITTING', isSubmitting });
  const setSubmitMessage = (message) => dispatch({ type: 'SET_SUBMIT_MESSAGE', message });
  const resetForm = () => dispatch({ type: 'RESET_FORM' });

  const setFormErrors = (errors) =>
    dispatch({
      type: 'SET_FORM_ERRORS',
      errors: typeof errors === 'function' ? errors(state.formErrors) : errors,
    });

  const setModifiedFields = (fields) =>
    dispatch({
      type: 'SET_MODIFIED_FIELDS',
      fields: typeof fields === 'function' ? fields(state.modifiedFields) : fields,
    });

  const value = {
    ...state,
    brand,               // { label, accent, logo, logoDark } — resolved by the page
    setFieldValue,
    setFile,
    setFormErrors,
    setCurrentStep,
    markStepCompleted,
    setSubmitting,
    setSubmitMessage,
    setModifiedFields,
    resetForm,
  };

  return (
    <FormDepartureContext.Provider value={value}>
      {children}
    </FormDepartureContext.Provider>
  );
};

export default FormDepartureContext;